import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Camera, Check, MapPin, Loader2, Sparkles, AlertTriangle, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

import { hostCardStyle } from '../../components/HostSettingsCard';
import { ExperiencePinMap } from '../../components/host/ExperiencePinMap';
import { HostExperienceGuestPreview } from '../../components/host/HostExperienceGuestPreview';
import {
  EXPERIENCE_CATEGORIES,
  EXPERIENCE_TAG_MAX,
  EXPERIENCE_DESCRIPTION_MAX,
  experienceTagsFor,
  getExperienceCategory,
} from '../../utils/experienceCategories';
import { readExifGps } from '../../utils/exifGps';
import {
  createExperience,
  deleteMyExperience,
  fetchHostListingCenter,
  fetchMyExperiences,
  uploadExperiencePhoto,
} from '../../utils/experiencesApi';
import { AddressAutocomplete } from '../../components/venue/onboarding/AddressAutocomplete';

const FONT = "'Inria Serif', serif";

const sectionLabel = {
  margin: '0 0 10px 4px',
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--accent-purple)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const inputShell = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'var(--surface)',
  border: '1.5px solid var(--card-border)',
  borderRadius: 16,
  padding: '12px 14px',
  boxShadow: 'var(--premium-shadow-nested)',
};

const inputControl = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: FONT,
  fontSize: 14.5,
  color: 'var(--text)',
  minWidth: 0,
};

function categoryChipStyle(isActive, color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 15px',
    borderRadius: 14,
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    border: `1.5px solid ${isActive ? color : 'var(--card-border)'}`,
    background: isActive ? color : 'var(--surface-2)',
    color: isActive ? '#FFFFFF' : 'var(--text)',
    boxShadow: isActive ? '0 6px 16px rgba(20, 16, 50, 0.16)' : 'none',
  };
}

function tagChipStyle(isActive) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 11,
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    border: isActive ? '1.5px solid var(--accent-purple)' : '1.5px solid var(--card-border)',
    background: isActive ? 'var(--accent-purple)' : 'var(--surface-2)',
    color: isActive ? '#FFFFFF' : 'var(--text)',
    boxShadow: isActive ? 'var(--premium-shadow-main)' : 'none',
  };
}

const statusPill = (status) => { // eslint-disable-line no-unused-vars
  const map = {
    pending: { label: 'Pending review', bg: 'rgba(231, 158, 38, 0.18)', color: '#B45309' },
    approved: { label: 'Live on the map', bg: 'rgba(31, 142, 42, 0.18)', color: '#166534' },
    rejected: { label: 'Declined', bg: 'rgba(196, 18, 48, 0.16)', color: '#991B1B' },
  };
  return map[status] || map.pending;
};

export default function HostAddExperiencePage() {
  const navigate = useNavigate();

  const [center, setCenter] = useState(null); // { latitude, longitude, listing_id }
  const [pin, setPin] = useState({ latitude: null, longitude: null });
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [placeQuery, setPlaceQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const fileInputRef = useRef(null);

  // Load listing center + my previous submissions on mount.
  useEffect(() => {
    (async () => {
      try {
        const c = await fetchHostListingCenter();
        setCenter(c);
        setPin({ latitude: c.latitude, longitude: c.longitude });
      } catch {
        // Silently accept — the map falls back to Athens center.
      }
      try {
        const items = await fetchMyExperiences();
        setMine(Array.isArray(items) ? items : []);
      } catch {
        setMine([]);
      }
    })();
  }, []);

  const handlePinChange = useCallback((lat, lng) => {
    setPin({ latitude: lat, longitude: lng });
  }, []);

  const handlePhoto = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));

    // Try EXIF GPS auto-drop before uploading.
    try {
      const gps = await readExifGps(file);
      if (gps) {
        setPin({ latitude: gps.latitude, longitude: gps.longitude });
        toast.success('Pin placed from your photo — drag to fine-tune.');
      }
    } catch {
      /* silent */
    }

    setPhotoUploading(true);
    try {
      const { photo_url } = await uploadExperiencePhoto(file);
      setPhotoFile({ file, photo_url });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
      setPhotoFile(null);
      setPhotoPreview('');
    } finally {
      setPhotoUploading(false);
    }
  }, []);

  const handleW3wLookup = null;
  const handlePlacePick = useCallback((feature) => {
    if (!feature) return;
    if (typeof feature.latitude !== 'number' || typeof feature.longitude !== 'number') return;
    setPin({ latitude: feature.latitude, longitude: feature.longitude });
    setPlaceQuery(feature.title || feature.subtitle || '');
    toast.success(`Pin placed on ${feature.title || 'the selected place'}`);
  }, []);

  const toggleTag = (tag) => {
    setTags((current) => {
      if (current.includes(tag)) return current.filter((t) => t !== tag);
      if (current.length >= EXPERIENCE_TAG_MAX) {
        toast(`Pick up to ${EXPERIENCE_TAG_MAX} tags. Remove one first.`, {
          id: 'exp-tag-cap',
          duration: 2200,
        });
        return current;
      }
      return [...current, tag];
    });
  };

  const availableTags = useMemo(() => (category ? experienceTagsFor(category) : []), [category]);
  const selectedCategoryObj = category ? getExperienceCategory(category) : null;

  // The first declined experience becomes a "needs fixing" card at the
  // top so the host has one-tap recovery. We keep the id around so we
  // can delete it when the resubmit succeeds (or when the host taps
  // Dismiss).
  const declinedExp = useMemo(
    () => (mine || []).find((m) => m.status === 'rejected') || null,
    [mine],
  );
  const [fixupId, setFixupId] = useState(''); // id being edited/resubmitted

  const declineReasonCopy = (reason) => {
    if (reason === 'photo') return { title: 'Try a stronger photo', body: 'The photo wasn\'t strong enough. Try a clearer, more scenic shot.' };
    if (reason === 'description') return { title: 'Add more detail', body: 'The description wasn\'t detailed enough. Add a short story or context.' };
    return { title: 'Needs a small fix', body: 'An admin flagged your experience — tweak it and resubmit.' };
  };

  const startFixup = useCallback((exp) => {
    if (!exp) return;
    setFixupId(exp.id);
    setCategory(exp.category || '');
    setTags(Array.isArray(exp.tags) ? exp.tags.slice(0, EXPERIENCE_TAG_MAX) : []);
    setDescription(exp.description || '');
    if (typeof exp.latitude === 'number' && typeof exp.longitude === 'number') {
      setPin({ latitude: exp.latitude, longitude: exp.longitude });
    }
    if (exp.photo_url) {
      // Keep the previous photo_url so the host can resubmit as-is if
      // only the description was flagged. They can Replace it via the
      // photo picker.
      setPhotoFile({ photo_url: exp.photo_url });
      setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/experiences/photo/${exp.photo_url}`);
    }
    // Scroll to top so the form is visible after prefill.
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
    toast.success('Loaded your previous submission — tweak and resubmit.');
  }, []);

  const dismissDeclined = useCallback(async (exp) => {
    if (!exp) return;
    try {
      await deleteMyExperience(exp.id);
      setMine((current) => current.filter((m) => m.id !== exp.id));
      if (fixupId === exp.id) setFixupId('');
      toast.success('Dismissed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not dismiss');
    }
  }, [fixupId]);

  const distanceFromListingKm = useMemo(() => {
    if (!center || pin.latitude == null || pin.longitude == null) return null;
    const R = 6371;
    const dLat = (pin.latitude - center.latitude) * Math.PI / 180;
    const dLng = (pin.longitude - center.longitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(center.latitude * Math.PI / 180)
        * Math.cos(pin.latitude * Math.PI / 180)
        * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, [center, pin]);

  const canSubmit = (
    !!category
    && pin.latitude != null && pin.longitude != null
    && description.trim().length > 0
    && !!photoFile?.photo_url
    && !photoUploading
    && !submitting
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Please complete every step above');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createExperience({
        latitude: pin.latitude,
        longitude: pin.longitude,
        category,
        tags,
        description: description.trim(),
        photo_url: photoFile.photo_url,
        listing_id: center?.listing_id || '',
      });
      // If we were fixing a previously-declined submission, delete the
      // old rejected doc so the top-of-page card disappears and the
      // guest map stays deduped.
      if (fixupId) {
        try { await deleteMyExperience(fixupId); } catch { /* noop */ }
      }
      setMine((current) => [
        created,
        ...current.filter((m) => m.id !== fixupId),
      ]);
      setFixupId('');
      toast.success('Submitted! An admin will review it shortly.');
      // Reset for a second submission.
      setCategory('');
      setTags([]);
      setDescription('');
      setPhotoFile(null);
      setPhotoPreview('');
      setPlaceQuery('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell" data-testid="host-add-experience-page" style={{ minHeight: '100vh', background: 'var(--app-bg)', paddingBottom: 60 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="host-add-experience-back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <ChevronLeft size={28} color="var(--text)" strokeWidth={1.6} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="var(--accent-purple)" />
          <h1 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Refer an Experience
          </h1>
        </div>
      </div>

      <div style={{ padding: '10px 20px 0' }}>
        {declinedExp ? (
          <div
            data-testid="host-declined-fixup-card"
            style={{
              background: 'linear-gradient(135deg, rgba(196,18,48,0.10), rgba(196,18,48,0.02))',
              border: '1.5px solid rgba(196,18,48,0.35)',
              borderRadius: 18,
              padding: '14px 14px',
              marginBottom: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              boxShadow: '0 8px 22px rgba(196,18,48,0.10)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: 'rgba(196,18,48,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <AlertTriangle size={18} color="#B91C1C" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: FONT, fontSize: 15, fontWeight: 700, color: '#7F1D1D' }}>
                  {declineReasonCopy(declinedExp.rejected_reason).title}
                </p>
                <p style={{ margin: '2px 0 0', fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>
                  {declineReasonCopy(declinedExp.rejected_reason).body}
                </p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button
                type="button"
                onClick={() => dismissDeclined(declinedExp)}
                data-testid="host-declined-fixup-dismiss"
                style={{
                  padding: '11px 12px', borderRadius: 12,
                  border: '1.5px solid rgba(196,18,48,0.30)',
                  background: '#FFFFFF', color: '#B91C1C',
                  fontFamily: FONT, fontSize: 13.5, fontWeight: 700,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6, minHeight: 44,
                }}
              >
                <Trash2 size={14} />
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => startFixup(declinedExp)}
                data-testid="host-declined-fixup-edit"
                style={{
                  padding: '11px 12px', borderRadius: 12,
                  border: 'none',
                  background: 'var(--accent-purple)', color: '#FFFFFF',
                  fontFamily: FONT, fontSize: 13.5, fontWeight: 700,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6, minHeight: 44,
                  boxShadow: '0 6px 16px rgba(112,63,246,0.28)',
                }}
              >
                <Wand2 size={14} />
                {declinedExp.rejected_reason === 'photo' ? 'Try a better image' : 'Edit & resubmit'}
              </button>
            </div>
          </div>
        ) : null}
        <p style={{ fontFamily: FONT, fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.45, margin: '4px 0 6px' }}>
          Share a spot that makes your area worth remembering — a hidden beach, a viewpoint, a walking trail. Guests will discover it on the Allure map.
        </p>
        <div
          data-testid="host-add-experience-monetisation-note"
          style={{
            background: 'var(--accent-surface-soft, rgba(112, 63, 246, 0.08))',
            border: '1px solid rgba(112, 63, 246, 0.20)',
            borderRadius: 14,
            padding: '10px 12px',
            fontFamily: FONT,
            fontSize: 12.5,
            color: 'var(--text-2)',
            lineHeight: 1.4,
            marginTop: 6,
          }}
        >
          Free for now. Once 20+ experiences appear within a 20 km radius of your listing, guests will pay to unlock the local experience package — and you'll earn every time your guests unlock it.
        </div>
      </div>

      {/* Category */}
      <div style={{ padding: '20px 20px 0' }}>
        <p style={sectionLabel}>Category · pick one</p>
        <div data-testid="host-add-experience-categories" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EXPERIENCE_CATEGORIES.map((cat) => {
            const isActive = category === cat.slug;
            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => { setCategory(cat.slug); setTags([]); }}
                data-testid={`host-add-experience-category-${cat.slug}`}
                aria-pressed={isActive}
                style={categoryChipStyle(isActive, cat.color)}
              >
                <span style={{ fontSize: 14 }}>{cat.emoji}</span>
                {cat.display}
                {isActive ? <Check size={13} strokeWidth={3} /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tags (only after a category is picked) */}
      {category ? (
        <div style={{ padding: '20px 20px 0' }}>
          <p style={sectionLabel}>Tags · up to {EXPERIENCE_TAG_MAX}</p>
          <div data-testid="host-add-experience-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {availableTags.map((tag) => {
              const isActive = tags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  data-testid={`host-add-experience-tag-${tag.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  style={tagChipStyle(isActive)}
                >
                  {isActive ? <Check size={11} strokeWidth={3} /> : null}
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Map */}
      <div style={{ padding: '22px 20px 0' }}>
        <p style={sectionLabel}>Where is the experience?</p>
        <ExperiencePinMap
          latitude={pin.latitude}
          longitude={pin.longitude}
          onChange={handlePinChange}
          height={300}
          pinColor={selectedCategoryObj?.color || '#7C3AED'}
        />
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => center && setPin({ latitude: center.latitude, longitude: center.longitude })}
            data-testid="host-add-experience-recenter"
            style={{ ...inputShell, width: 'auto', padding: '8px 12px', cursor: 'pointer', gap: 6, fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: 'var(--accent-purple)' }}
          >
            <MapPin size={14} />
            Center on my listing
          </button>
          {distanceFromListingKm != null ? (
            <span
              data-testid="host-add-experience-distance"
              style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: 'var(--text-2)' }}
            >
              {distanceFromListingKm < 1
                ? `${Math.round(distanceFromListingKm * 1000)} m from your listing`
                : `${distanceFromListingKm.toFixed(1)} km from your listing`}
            </span>
          ) : null}
        </div>

        {/* Search by name or address */}
        <div style={{ marginTop: 14 }}>
          <AddressAutocomplete
            value={placeQuery}
            onChange={setPlaceQuery}
            onSelect={handlePlacePick}
            placeholder="e.g. Peristeria Gorge"
            testId="host-add-experience-place-search"
            lat={center?.latitude}
            lon={center?.longitude}
            label="Search by name or address"
            helperText="Type any spot, landmark or address near your listing"
          />
        </div>
      </div>

      {/* Photo */}
      <div style={{ padding: '22px 20px 0' }}>
        <p style={sectionLabel}>Photo · one required</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handlePhoto(e.target.files?.[0])}
          data-testid="host-add-experience-photo-input"
          style={{ display: 'none' }}
        />
        {photoPreview ? (
          <div
            data-testid="host-add-experience-photo-preview"
            style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', border: '1.5px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }}
          >
            <img src={photoPreview} alt="Experience preview" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
            {photoUploading ? (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(20,16,50,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF', fontFamily: FONT, fontWeight: 700, fontSize: 13, gap: 8 }}>
                <Loader2 size={16} className="animate-spin" /> Uploading…
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="host-add-experience-photo-replace"
              style={{ position: 'absolute', top: 10, right: 10, padding: '6px 10px', borderRadius: 999, border: 'none', background: 'rgba(20,16,50,0.72)', color: '#FFF', fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              Replace
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="host-add-experience-photo-picker"
            style={{ ...hostCardStyle, width: '100%', border: '1.5px dashed var(--card-border)', background: 'var(--surface-2)', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          >
            <Camera size={30} color="var(--accent-purple)" />
            <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add one photo</p>
            <p style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--text-2)', margin: 0, textAlign: 'center', lineHeight: 1.35 }}>
              We only accept one — keeps the mystery for your guests. If your photo has GPS metadata we'll place the pin for you.
            </p>
          </button>
        )}
      </div>

      {/* Description */}
      <div style={{ padding: '22px 20px 0' }}>
        <p style={sectionLabel}>Description · shown to guests</p>
        <div style={inputShell}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, EXPERIENCE_DESCRIPTION_MAX))}
            maxLength={EXPERIENCE_DESCRIPTION_MAX}
            placeholder="A short story: what makes this spot worth going to?"
            data-testid="host-add-experience-description"
            rows={3}
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck
            style={{ ...inputControl, resize: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <span data-testid="host-add-experience-description-counter" style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: 'var(--text-3)' }}>
            {description.length}/{EXPERIENCE_DESCRIPTION_MAX}
          </span>
        </div>
      </div>

      {/* Guest-map preview — shows the host EXACTLY how their card
          will render on the Guest Map (same 160px cover crop as
          ExperiencePreviewSheet). Lets them spot photo-crop
          problems before submitting. Only visible once a photo
          and category are picked so it stays meaningful. */}
      {photoPreview && category ? (
        <div style={{ padding: '22px 20px 0' }}>
          <p style={sectionLabel}>Guest map preview · exactly what guests see</p>
          <HostExperienceGuestPreview
            category={category}
            description={description}
            photoPreviewUrl={photoPreview}
            tags={tags}
          />
          <p style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--text-2)', margin: '8px 2px 0', lineHeight: 1.4 }}>
            Not happy with how your photo is cropped here? Tap <span style={{ fontWeight: 700, color: 'var(--text)' }}>Replace</span> on the photo above and pick a wider shot — the card always crops to this exact box.
          </p>
        </div>
      ) : null}

      {/* Submit */}
      <div style={{ padding: '24px 20px 8px' }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="host-add-experience-submit"
          style={{
            width: '100%',
            padding: '15px 18px',
            borderRadius: 9999,
            border: 'none',
            background: canSubmit ? 'var(--accent-purple)' : 'var(--card-border)',
            color: '#FFFFFF',
            fontFamily: FONT,
            fontSize: 18,
            fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            boxShadow: canSubmit ? 'var(--premium-shadow-main)' : 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          Submit for review
        </button>
      </div>

      {/* Pending & approved submissions are intentionally hidden here —
          the host only needs to see declined ones (as the fixup card
          at the top of the page) so they can quickly resubmit. */}
    </div>
  );
}
